import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Zap,
  Clock,
  CheckCircle,
  AlertCircle,
  Beaker,
  X,
  Rocket
} from 'lucide-react';
import { supabase } from '../../../shared/lib/supabase';
import { toast } from 'sonner';
import { calculateGenerationNumbers } from '../lib/generationUtils';

interface GenerationsSidebarProps {
  projectId: string;
  onOpenGeneration: (jobId: string, name: string) => void;
  onClose: () => void;
  onOpenWorkflow?: () => void;
}

interface OdysseyJob {
  job_id: string;
  name: string;
  status: string;
  created_at: string;
  project_id: string;
  workflow_type: string;
  generation_id?: string;
  base_job_id: string | null;
}

export function GenerationsSidebar({ projectId, onOpenGeneration, onClose, onOpenWorkflow }: GenerationsSidebarProps) {
  const [jobs, setJobs] = useState<OdysseyJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [generationNumbers, setGenerationNumbers] = useState<Map<string, number>>(new Map());

  const loadJobs = async () => {
    try {
      setLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Direct query to odyssey_jobs table ordered by created_at descending
      // Remove user filter to allow team members to see each other's jobs
      const { data: projectJobs, error } = await supabase
        .from('odyssey_jobs')
        .select('job_id, name, status, created_at, project_id, workflow_type, base_job_id, generation_id')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        throw error;
      }

      const jobs = projectJobs || [];
      setJobs(jobs);
      
      // Calculate generation numbers for all jobs regardless of status
      // Generation number is determined by parent-child relationships, not completion status
      const genNumbers = calculateGenerationNumbers(jobs);
      setGenerationNumbers(genNumbers);
    } catch (error) {
      console.error('Error loading jobs:', error);
      toast.error('Failed to load generations');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {

    loadJobs();

    // Set up real-time subscription for odyssey_jobs
    // Use static channel name to allow channel reuse
    const channelName = `odyssey-jobs-${projectId}`;

    // Filter at database level for performance
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
        schema: 'public',
        table: 'odyssey_jobs',
        filter: `project_id=eq.${projectId}`
      }, () => {
        loadJobs();
      })
      .subscribe((status, err) => {
        if (err) {
          console.error('❌ Subscription error:', err);
          console.error('❌ Error details:', JSON.stringify(err));
        } else {
          if (status === 'CHANNEL_ERROR') {
            console.error('Channel error - subscription failed');
          } else if (status === 'TIMED_OUT') {
            console.error('Subscription timed out');
          }
        }
      });
    
    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-green-600" />;
      case 'running':
      case 'processing':
        return <Clock className="h-3 w-3 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-3 w-3 text-red-600" />;
      default:
        return <Clock className="h-3 w-3 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'running':
        return 'text-blue-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-64 sidebar-width-secondary bg-white flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-gray-600" />
            <h3 className="text-base font-normal text-gray-900">Generations</h3>
          </div>
          <button
            onClick={onClose}
            className="ml-1 p-0.5 rounded-sm hover:bg-gray-200 transition-all flex-shrink-0"
            title="Close generations sidebar"
          >
            <X className="h-4 w-4 text-gray-500 hover:text-gray-700" />
          </button>
        </div>
        
        {/* Search Bar */}
        <div className="relative mt-4">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search generations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Separator */}
      <div className="border-b border-gray-200"></div>

      {/* Iterations List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Zap className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {searchQuery ? 'No generations found' : 'No generations yet'}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredJobs.map((job) => (
              <button
                key={job.job_id}
                onClick={() => onOpenGeneration(job.job_id, job.name)}
                className="w-full px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group border-b border-gray-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Rocket className="h-3 w-3 text-purple-500 flex-shrink-0" />
                      <span className="text-sm font-normal text-gray-900 truncate">
                        {job.name}
                      </span>
                      {job.generation_id && (
                        <span className="text-xs px-1 py-0.5 rounded-sm bg-purple-100 text-purple-700">
                          Iteration
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        {getStatusIcon(job.status)}
                        <span className={getStatusColor(job.status)}>
                          {job.status}
                        </span>
                      </div>
                      <span className="mx-1">•</span>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{formatRelativeTime(job.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  {/* Generation Number Badge - Right Side */}
                  <div className="flex items-center ml-2">
                    <span className="w-6 h-6 rounded-full bg-gray-200 text-black text-sm font-semibold flex items-center justify-center flex-shrink-0">
                      {generationNumbers.get(job.job_id) || 1}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
